import React from 'react'
import {
  Box,
  Grid,
  Pagination,
  Typography,
  Paper,
  CircularProgress
} from '@mui/material'
import OrderCard from 'pages/MyAccount/OrderCard'
import OrderDetails from 'pages/MyAccount/OrderDetails'
import { Order } from 'hooks/useOrder'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { useTranslation } from 'react-i18next'

type OrderSectionProps = {
  selectedOrder: Order | null
  orders: Order[]
  totalPages: number
  currentPage: number
  onViewOrderDetails: (order: Order) => void
  onBackToOrders: () => void
  onPageChange: (page: number) => void
  isLoading?: boolean
}

const MyOrder: React.FC<OrderSectionProps> = ({
  selectedOrder,
  orders,
  totalPages,
  currentPage,
  onViewOrderDetails,
  onBackToOrders,
  onPageChange,
  isLoading = false
}) => {
  const { t } = useTranslation()
  return (
    <Box>
      {selectedOrder ? (
        <OrderDetails order={selectedOrder} onBack={onBackToOrders} />
      ) : isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {orders.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                my: 8,
                textAlign: 'center',
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 3,
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ShoppingCartIcon
                sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }}
              />
              <Typography variant='h6' color='text.secondary' gutterBottom>
                {t('myAccount.orders.noOrders')}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {t('myAccount.orders.ordersWillAppear')}
              </Typography>
            </Paper>
          ) : (
            <Box>
              <Grid container>
                {orders.map(order => (
                  <Grid item xs={12} key={order.id}>
                    <OrderCard order={order} onClick={onViewOrderDetails} />
                  </Grid>
                ))}
              </Grid>

              {totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 4,
                    mb: 4
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, page) => onPageChange(page)}
                      color='primary'
                      size='large'
                      showFirstButton
                      showLastButton
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontWeight: 500,
                          fontSize: '0.95rem'
                        },
                        '& .Mui-selected': {
                          fontWeight: 700
                        }
                      }}
                    />
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default MyOrder
